/**
 * TimeTrex Taller - API Service
 * Conexión con Google Apps Script Backend
 */

// URL del backend GAS
const BASE_URL = 'https://script.google.com/macros/s/AKfycbx0PZNCkFqHP4hUAMZi9mg05zlZE0t-sPhl-2v4GOn6tlTryalUR95QAqis0SnQkfEP/exec';

interface RequestOptions {
  action: string;
  method?: 'GET' | 'POST';
  params?: Record<string, string>;
  body?: Record<string, unknown>;
  userId?: string;
}

async function request<T>(options: RequestOptions): Promise<T> {
  const { action, method = 'GET', params = {}, body } = options;

  const url = new URL(BASE_URL);
  url.searchParams.set('action', action);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) {
      url.searchParams.set(k, String(v));
    }
  });

  const requestOptions: RequestInit = {
    method,
    redirect: 'follow',
  };

  if (body && method === 'POST') {
    // Usamos text/plain para evitar CORS preflight en GAS
    // (application/json dispararía OPTIONS que GAS no puede manejar)
    requestOptions.headers = { 'Content-Type': 'text/plain;charset=utf-8' };
    requestOptions.body = JSON.stringify({
      ...body,
      action,
      userId: localStorage.getItem('userId'),
    });
  }

  try {
    const response = await fetch(url.toString(), requestOptions);
    const json = await response.json();

    if (!json.success) {
      throw new Error(json.error || 'Error en la solicitud');
    }

    return json.data as T;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

// ============== AUTH API ==============

export const authApi = {
  async login(userName: string, password: string) {
    // Login usa GET para evitar CORS preflight issues con GAS
    const data = await request<{ session_id: string; user: unknown }>({
      action: 'login',
      method: 'GET',
      params: { userName, password },
    });

    if (data) {
      localStorage.setItem('sessionId', data.session_id);
      localStorage.setItem('userId', (data.user as { id: string }).id);
      localStorage.setItem('user', JSON.stringify(data.user));
    }

    return data;
  },

  async logout() {
    await request({ action: 'logout', method: 'POST' });
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');
  },

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated() {
    return !!localStorage.getItem('sessionId');
  },
};

// ============== ATTENDANCE API ==============

export const attendanceApi = {
  async punchIn(location?: string, note?: string): Promise<unknown> {
    return request({ action: 'punchIn', method: 'POST', body: { location, note } });
  },

  async punchOut(location?: string, note?: string): Promise<unknown> {
    return request({ action: 'punchOut', method: 'POST', body: { location, note } });
  },

  async getPunches(startDate: string, endDate: string): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getPunches', params: { startDate, endDate } });
  },

  async getMyStatus(): Promise<unknown> {
    return request<unknown>({ action: 'getMyStatus' });
  },
};

// ============== WORKSHOP API ==============

export const workshopApi = {
  // Vehicles
  async createVehicle(data: Record<string, unknown>): Promise<unknown> {
    return request({ action: 'createVehicle', method: 'POST', body: { data } });
  },

  async getVehicles(placa?: string): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getVehicles', params: { placa: placa || '' } });
  },

  async getVehicle(id: string): Promise<unknown> {
    return request({ action: 'getVehicle', params: { id } });
  },

  // Jobs
  async createJob(data: Record<string, unknown>): Promise<unknown> {
    return request({ action: 'createJob', method: 'POST', body: { data } });
  },

  async getJobs(filters?: { estado_id?: number; priority_id?: number }): Promise<unknown[]> {
    const params: Record<string, string> = {};
    if (filters?.estado_id) params.estado_id = String(filters.estado_id);
    if (filters?.priority_id) params.priority_id = String(filters.priority_id);
    return request<unknown[]>({ action: 'getJobs', params });
  },

  async getJob(id: string): Promise<unknown> {
    return request({ action: 'getJob', params: { id } });
  },

  async updateJobEstado(jobId: string, estadoId: number): Promise<unknown> {
    return request({ action: 'updateJobEstado', method: 'POST', body: { jobId, estadoId } });
  },

  async assignOperario(jobId: string, operarioId: string): Promise<unknown> {
    return request({ action: 'assignOperario', method: 'POST', body: { jobId, operarioId } });
  },

  // Etapas
  async getEtapas(): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getEtapas' });
  },

  async getJobSteps(jobId: string): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getJobSteps', params: { jobId } });
  },

  async startStep(jobId: string, stepId: string): Promise<unknown> {
    return request({ action: 'startStep', method: 'POST', body: { jobId, stepId } });
  },

  async completeStep(jobId: string, stepId: string): Promise<unknown> {
    return request({ action: 'completeStep', method: 'POST', body: { jobId, stepId } });
  },

  // Activities
  async getActivities(jobId: string): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getActivities', params: { jobId } });
  },

  async startActivity(jobId: string, stepId: string, nota?: string): Promise<unknown> {
    return request({ action: 'startActivity', method: 'POST', body: { jobId, stepId, nota } });
  },

  async pauseActivity(activityId: string, tipo: 'pausa' | 'almuerzo'): Promise<unknown> {
    return request({ action: 'pauseActivity', method: 'POST', body: { activityId, tipo } });
  },

  async resumeActivity(activityId: string): Promise<unknown> {
    return request({ action: 'resumeActivity', method: 'POST', body: { activityId } });
  },

  async finishActivity(activityId: string, nota?: string): Promise<unknown> {
    return request({ action: 'finishActivity', method: 'POST', body: { activityId, nota } });
  },

  // Operario Status
  async getOperarioStatuses(): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getOperarioStatuses' });
  },

  async getMyOperarioStatus(): Promise<unknown> {
    return request<unknown>({ action: 'getMyOperarioStatus' });
  },

  async getMyCurrentJob(): Promise<unknown> {
    return request<unknown>({ action: 'getMyCurrentJob' });
  },

  async getMyAssignedJob(): Promise<unknown> {
    return request<unknown>({ action: 'getMyAssignedJob' });
  },

  async updateMyStatus(statusId: number, jobId?: string, nota?: string): Promise<unknown> {
    return request({ action: 'updateMyStatus', method: 'POST', body: { statusId, jobId, nota } });
  },

  // Operarios disponibles
  async getOperarios(): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getOperarios' });
  },
};

// ============== PERMISSIONS API ==============

export const permissionsApi = {
  async createRequest(data: {
    type_id: number;
    start_date: string;
    end_date: string;
    reason?: string;
  }): Promise<unknown> {
    return request({ action: 'createRequest', method: 'POST', body: { data } });
  },

  async getMyRequests(): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getMyRequests' });
  },

  async getPendingRequests(): Promise<unknown[]> {
    return request<unknown[]>({ action: 'getPendingRequests' });
  },

  async approveRequest(requestId: string, note?: string): Promise<unknown> {
    return request({ action: 'approveRequest', method: 'POST', body: { requestId, note } });
  },

  async denyRequest(requestId: string, reason?: string): Promise<unknown> {
    return request({ action: 'denyRequest', method: 'POST', body: { requestId, reason } });
  },

  async cancelRequest(requestId: string): Promise<unknown> {
    return request({ action: 'cancelRequest', method: 'POST', body: { requestId } });
  },
};

// ============== REPORTS API ==============

export const reportsApi = {
  async getEfficiencyReport(startDate: string, endDate: string): Promise<unknown> {
    return request({ action: 'getEfficiencyReport', params: { startDate, endDate } });
  },

  async getOperarioReport(userId: string, startDate: string, endDate: string): Promise<unknown> {
    return request({ action: 'getOperarioReport', params: { userId, startDate, endDate } });
  },

  async getJobTimeReport(jobId: string): Promise<unknown> {
    return request({ action: 'getJobTimeReport', params: { jobId } });
  },
};

// ============== USERS API ==============

export const usersApi = {
  async getUsers(filters?: { role_id?: number }): Promise<unknown[]> {
    const params: Record<string, string> = {};
    if (filters?.role_id) params.role_id = String(filters.role_id);
    return request<unknown[]>({ action: 'getUsers', params });
  },
};